import { z } from "zod";

import {
  parseIdentitySummary,
  type IdentitySummaryV1,
} from "../identityContracts";

const digestSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);
const tokenSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]*$/);
const availabilitySchema = z.enum([
  "available",
  "partial",
  "not_recorded",
  "failed",
  "deleted",
  "expired",
  "unsupported",
  "mismatched",
  "redacted",
  "truncated",
  "pending",
  "unavailable",
  "projection_only",
  "evidence_available",
  "correlation_mismatch",
]);
const reasonSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
const ppmSchema = z.number().int().min(0).max(1_000_000);

const manifestExtrasSchema = z.object({
  echo_id: tokenSchema.nullable(),
  workload_id: tokenSchema.nullable(),
  identity_run_id: tokenSchema.nullable(),
  identity_manifest_id: tokenSchema.nullable(),
  media_set_digest: digestSchema.nullable(),
  config_release_id: tokenSchema.nullable(),
  config_digest: digestSchema.nullable(),
  model_release_ids: z.record(tokenSchema, tokenSchema),
  occurred_at: z.string().nullable(),
  downstream_counts: z
    .record(z.string(), z.number().int().nonnegative())
    .default({}),
  qc_counts: z.record(z.string(), z.number().int().nonnegative()).default({}),
  scene_ids: z.array(tokenSchema).max(32).default([]),
  subjects_truncated: z.boolean().default(false),
  artifact_availability: z.string().default("not_recorded"),
  artifact_count: z.number().int().nonnegative().default(0),
  artifact_kinds: z.array(tokenSchema).max(32).default([]),
});

export type IdentityManifestHistoryItem = IdentitySummaryV1 &
  z.infer<typeof manifestExtrasSchema>;

export type IdentityManifestHistory = {
  data: IdentityManifestHistoryItem[];
  total: number;
  truncated: boolean;
};

export function parseIdentityManifestHistory(
  value: unknown,
): IdentityManifestHistory {
  const envelope = z
    .object({
      data: z.array(z.record(z.string(), z.unknown())).max(64),
      total: z.number().int().nonnegative(),
      truncated: z.boolean(),
    })
    .parse(value);
  return {
    ...envelope,
    data: envelope.data.map((item) => ({
      ...parseIdentitySummary(item),
      ...manifestExtrasSchema.parse(item),
    })),
  };
}

export const signedIdentityArtifactSchema = z.object({
  artifact_id: tokenSchema,
  kind: z.enum(["identity_report", "identity_contact_sheet"]),
  content_digest: digestSchema,
  byte_size: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024),
  url: z
    .string()
    .url()
    .refine(
      (value) => value.startsWith("https://"),
      "signed URL must use HTTPS",
    ),
  expires_in: z.number().int().positive().max(900),
});

export type SignedIdentityArtifact = z.infer<
  typeof signedIdentityArtifactSchema
>;

const inspectorSubjectSchema = z.object({
  echo_identity_id: tokenSchema,
  species: z.string(),
  confidence: z.string(),
  resolution_status: z.string(),
  distinct_media_count: z.number().int().nonnegative(),
  distinct_tracklet_count: z.number().int().nonnegative(),
  reference_count: z.number().int().nonnegative(),
});

const inspectorSchema = z.object({
  schema_version: z.literal("identity-inspector.v1"),
  availability: availabilitySchema,
  session_id: tokenSchema,
  echo_identity_id: tokenSchema,
  manifest_version: z.number().int().positive(),
  manifest_digest: digestSchema.nullable(),
  identity_run_id: tokenSchema,
  subject: inspectorSubjectSchema.nullable(),
  artifact_availability: z.enum([
    "available",
    "partial",
    "not_recorded",
    "deleted",
    "unknown",
    "unavailable",
  ]),
  artifacts: z.array(signedIdentityArtifactSchema).max(32),
  reason_code: reasonSchema.nullable(),
});

export type IdentityInspector = z.infer<typeof inspectorSchema>;

export function parseIdentityInspector(
  value: unknown,
  expected: {
    sessionId: string;
    identityId: string;
    manifestVersion: number;
    identityRunId: string;
  },
): IdentityInspector {
  const envelope = z.object({ data: inspectorSchema }).parse(value);
  const parsed = envelope.data;
  if (
    parsed.session_id !== expected.sessionId ||
    parsed.echo_identity_id !== expected.identityId ||
    parsed.manifest_version !== expected.manifestVersion ||
    parsed.identity_run_id !== expected.identityRunId
  ) {
    throw new Error("identity_inspector_correlation_mismatch");
  }
  return parsed;
}

const reportForbiddenMarkers = [
  "embedding",
  "vector",
  "display_label",
  "person_name",
  "safe_appearance",
  "signed_url",
  "storage",
  "bucket",
  "source_content_digest",
  "detector_receipt",
  "mask_ref",
  "url",
  "uri",
  "path",
  "name",
];

function scanPrivateReport(value: unknown, path = "$") {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      scanPrivateReport(item, `${path}[${index}]`),
    );
    return;
  }
  if (typeof value === "string") {
    if (/^(?:https?|s3|gs|file):\/\//i.test(value)) {
      throw new Error(`identity_report_forbidden_locator:${path}`);
    }
    return;
  }
  if (value === null || typeof value !== "object") return;
  for (const [key, item] of Object.entries(value)) {
    const lowered = key.toLowerCase();
    if (reportForbiddenMarkers.some((marker) => lowered.includes(marker))) {
      throw new Error(`identity_report_forbidden_key:${path}.${key}`);
    }
    scanPrivateReport(item, `${path}.${key}`);
  }
}

const referenceSchema = z.object({
  reference_id: tokenSchema,
  source_media_id: tokenSchema,
  instance_id: tokenSchema,
  view: z.string(),
  quality: z.string(),
});
const breedCandidateSchema = z.object({
  breed_key: tokenSchema,
  probability_ppm: ppmSchema,
});
const breedObservationSchema = z.object({
  schema_version: z.literal("dog-breed-observation.v1"),
  status: z.enum(["observed", "abstained", "unavailable", "failed", "unknown"]),
  claim_scope: z.literal("visual_resemblance"),
  breed_key: tokenSchema.nullable(),
  top_candidates: z.array(breedCandidateSchema).max(3),
  top1_probability_ppm: ppmSchema.nullable(),
  top1_margin_ppm: ppmSchema.nullable(),
  agreement_ppm: ppmSchema.nullable(),
  normalized_entropy_ppm: ppmSchema.nullable(),
  sampled_crop_count: z.number().int().nonnegative().max(16),
  independent_support_count: z.number().int().nonnegative().max(16),
  distinct_media_count: z.number().int().nonnegative().max(16),
  view_diversity_count: z.number().int().nonnegative().max(8),
  reasons: z.array(reasonSchema).max(20),
  classifier_registry_id: tokenSchema,
  artifact_digest: digestSchema,
  taxonomy_digest: digestSchema,
  preprocessor_digest: digestSchema,
  calibration_digest: digestSchema,
});
const subjectSchema = z.object({
  echo_identity_id: tokenSchema,
  kind: z.literal("pet"),
  species: z.literal("dog"),
  confidence: z.string(),
  resolution_status: z.string(),
  cluster_support_count: z.number().int().nonnegative(),
  distinct_media_count: z.number().int().nonnegative(),
  distinct_tracklet_count: z.number().int().nonnegative(),
  references: z.array(referenceSchema).max(16),
  reference_count: z.number().int().nonnegative(),
  views: z.array(z.string()).max(8),
  decision_ids: z.array(tokenSchema).max(100),
  breed_observation: breedObservationSchema.nullable().optional().default(null),
});
const instanceSchema = z.object({
  instance_id: tokenSchema,
  source_media_id: tokenSchema,
  frame_index: z.number().int().nonnegative().nullable(),
  timestamp_us: z.number().int().nonnegative().nullable(),
  time_range_us: z
    .tuple([z.number().int().nonnegative(), z.number().int().positive()])
    .nullable(),
  box: z.object({
    x_ppm: ppmSchema,
    y_ppm: ppmSchema,
    width_ppm: ppmSchema,
    height_ppm: ppmSchema,
  }),
  detector_confidence_ppm: ppmSchema,
  quality: z.object({
    band: z.string(),
    size_ppm: ppmSchema,
    blur_ppm: ppmSchema,
    occlusion_ppm: ppmSchema,
    truncation_ppm: ppmSchema,
    illumination: z.string(),
    view: z.string(),
  }),
  tracklet_id: tokenSchema.nullable(),
  assigned_echo_identity_id: tokenSchema.nullable(),
  assignment_status: z.string(),
  candidate_reasons: z.array(reasonSchema).max(20),
  confidence: z.string(),
});
const trackletSchema = z.object({
  tracklet_id: tokenSchema,
  source_media_id: tokenSchema,
  instance_ids: z.array(tokenSchema).min(1).max(500),
  start_us: z.number().int().nonnegative(),
  end_us: z.number().int().positive(),
  quality: z.string(),
  view_coverage: z.array(z.string()).max(8),
});
const assignmentSchema = z.object({
  assignment_id: tokenSchema,
  echo_identity_id: tokenSchema.nullable(),
  instance_ids: z.array(tokenSchema).max(500),
  tracklet_ids: z.array(tokenSchema).max(100),
  status: z.string(),
  probability_ppm: ppmSchema.nullable(),
  confidence: z.string(),
  top_candidate_ppm: ppmSchema.nullable(),
  margin_ppm: ppmSchema.nullable(),
  support_evidence_count: z.number().int().nonnegative(),
  view_diversity_count: z.number().int().nonnegative(),
  reasons: z.array(reasonSchema).min(1).max(20),
});
const decisionSchema = z.object({
  decision_id: tokenSchema,
  decision_type: z.string(),
  actor: z.string(),
  actor_ref: tokenSchema.nullable(),
  instance_ids: z.array(tokenSchema).max(500),
  tracklet_ids: z.array(tokenSchema).max(100),
  subject_ids: z.array(tokenSchema).max(100),
  reasons: z.array(reasonSchema).min(1).max(20),
  downstream_result: z.string(),
  created_at: z.string(),
});

const reportSchema = z.object({
  schema_version: z.literal("identity-report.v1"),
  environment: z.enum(["staging", "stg"]),
  product_flow: z.literal("b2c"),
  echo_id: tokenSchema,
  session_id: tokenSchema,
  workload_id: tokenSchema,
  identity_run_id: tokenSchema,
  identity_manifest_id: tokenSchema,
  manifest_version: z.number().int().positive(),
  manifest_digest: digestSchema,
  manifest_status: z.string(),
  media_set_digest: digestSchema,
  config_release_id: tokenSchema,
  config_digest: digestSchema,
  model_release_ids: z.record(tokenSchema, tokenSchema),
  subjects: z.array(subjectSchema).max(100).default([]),
  instances: z.array(instanceSchema).max(512).default([]),
  tracklets: z.array(trackletSchema).max(256).default([]),
  assignments: z.array(assignmentSchema).max(512).default([]),
  decisions: z.array(decisionSchema).max(512).default([]),
  subject_count: z.number().int().nonnegative(),
  instance_count: z.number().int().nonnegative(),
  tracklet_count: z.number().int().nonnegative(),
  assignment_count: z.number().int().nonnegative(),
  unknown_instance_count: z.number().int().nonnegative(),
  decision_count: z.number().int().nonnegative(),
  truncation: z.object({
    instances: z.boolean(),
    tracklets: z.boolean(),
    assignments: z.boolean(),
    decisions: z.boolean(),
  }),
  panel_availability: z.object({
    candidate_comparison: availabilitySchema,
    pairwise_matrix: availabilitySchema,
    track_timeline: availabilitySchema,
    decision_history: availabilitySchema,
  }),
  quality_summary: z.object({
    availability: z.string(),
    accepted_assignment_count: z.number().int().nonnegative(),
    abstained_assignment_count: z.number().int().nonnegative(),
    unknown_instance_count: z.number().int().nonnegative(),
    cannot_link_count: z.number().int().nonnegative(),
    constraint_conflict_count: z.number().int().nonnegative(),
    failed_asset_count: z.number().int().nonnegative(),
    reasons: z.array(reasonSchema).max(30),
  }),
  human_rows_redacted: z.number().int().nonnegative(),
  created_at: z.string(),
});

export type IdentityReport = z.infer<typeof reportSchema>;

export function parseIdentityReport(
  value: unknown,
  expected: {
    sessionId: string;
    manifestVersion: number;
    manifestDigest: string;
    identityRunId: string;
  },
): IdentityReport {
  scanPrivateReport(value);
  const report = reportSchema.parse(value);
  if (
    report.session_id !== expected.sessionId ||
    report.manifest_version !== expected.manifestVersion ||
    report.manifest_digest !== expected.manifestDigest ||
    report.identity_run_id !== expected.identityRunId
  ) {
    throw new Error("identity_report_correlation_mismatch");
  }
  if (
    report.subject_count < report.subjects.length ||
    report.instance_count < report.instances.length ||
    report.tracklet_count < report.tracklets.length ||
    report.assignment_count < report.assignments.length ||
    report.decision_count < report.decisions.length
  ) {
    throw new Error("identity_report_count_inconsistent");
  }
  return report;
}
